/**
 * THE PANTHEON ⚡
 * 
 * The divine command center where AI gods unite.
 * Home of Athena (Intelligence), Hermes (Content), and Apollo (Distribution).
 * 
 * Features:
 * - THE WAR COUNCIL: Live multi-agent chat where gods debate strategy
 * - Divine Synergy Score: Real-time collaboration effectiveness
 * - One-Click Divine Strike: Full automation pipeline
 * - Active Gods: Overview of all agents with real timestamps
 * - Joint Divine Briefing: Combined intelligence reports
 * - Divine Metrics: Before/After performance tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Zap, Target, TrendingUp, ArrowRight, Play, Pause,
  Shield, Sword, Eye, Clock, AlertCircle, CheckCircle,
  BarChart3, Activity, Sparkles, RefreshCw, ChevronRight,
  FileText, MessageSquare, Mail, Image, ExternalLink,
  Users, Minimize2, Maximize2, Crown, Star
} from 'lucide-react';

// Import the War Council Chat
import WarCouncilChat from './WarCouncilChat';

// Import the Living Pantheon
import LivingPantheon from './LivingPantheon';

// Import the Living War Room
import LivingWarRoom from './LivingWarRoom';

// Import Leaderboard
import Leaderboard from './Leaderboard';

// Import Autonomous Debates
import AutonomousDebates from './AutonomousDebates';

// Import Strategic Briefing
import StrategicBriefing from './StrategicBriefing';

// Import Consciousness Panel
import { ConsciousnessDetailPanel, ConsciousnessAvatar } from './ConsciousnessPanel';
import { useEssenceReward } from '../../hooks/useEssenceReward';
import { ConsciousnessMilestoneModal } from './ConsciousnessMilestoneModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pre-generate divine particle positions (stable across renders)
const DIVINE_PARTICLES = Array.from({ length: 30 }, (_, i) => ({
  left: (i * 3 + (i % 7) * 11) % 100,
  top: (i * 5 + (i % 5) * 13) % 100,
  isOrange: i % 2 === 0,
  duration: 3 + (i % 5),
  delay: (i * 0.17) % 5
}));

// Divine Energy Particles
const DivineEnergyParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {DIVINE_PARTICLES.map((particle, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            left: `${particle.left}%`,
            top: `${particle.top}%`,
            background: particle.isOrange ? '#f97316' : '#a855f7',
            boxShadow: particle.isOrange ? '0 0 10px #f97316' : '0 0 10px #a855f7',
            animation: `float-divine ${particle.duration}s ease-in-out infinite`,
            animationDelay: `${particle.delay}s`
          }}
        />
      ))}
      <style>{`
        @keyframes float-divine {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
          50% { transform: translate(20px, -30px) scale(1.5); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

// Divine Synergy Score Display
const SynergyScore = ({ score = 85 }) => {
  const getScoreColor = (s) => {
    if (s >= 90) return 'from-green-400 to-emerald-500';
    if (s >= 70) return 'from-yellow-400 to-amber-500';
    if (s >= 50) return 'from-orange-400 to-red-500';
    return 'from-red-500 to-pink-500';
  };

  const getScoreLabel = (s) => {
    if (s >= 90) return 'Divine Harmony';
    if (s >= 70) return 'Strong Alliance';
    if (s >= 50) return 'Growing Bond';
    return 'Awakening';
  };

  return (
    <div className="relative">
      {/* Circular progress */}
      <div className="relative w-48 h-48 mx-auto">
        <svg className="w-full h-full transform -rotate-90">
          {/* Background circle */}
          <circle
            cx="96"
            cy="96"
            r="88"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            className="text-gray-800"
          />
          {/* Progress circle */}
          <circle
            cx="96"
            cy="96"
            r="88"
            stroke="url(#synergyGradient)"
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${(score / 100) * 553} 553`}
            className="transition-all duration-1000 ease-out"
          />
          <defs>
            <linearGradient id="synergyGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#a855f7" />
              <stop offset="50%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#eab308" />
            </linearGradient>
          </defs>
        </svg>
        
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="flex items-center gap-1">
            <span className="text-4xl">🦉</span>
            <Zap className="w-6 h-6 text-yellow-400 animate-pulse" />
            <span className="text-4xl">👟</span>
          </div>
          <div className={`text-4xl font-black bg-gradient-to-r ${getScoreColor(score)} bg-clip-text text-transparent mt-2`}>
            {score}%
          </div>
          <div className="text-sm text-gray-400 mt-1">{getScoreLabel(score)}</div>
        </div>
      </div>
    </div>
  );
};

// Intelligence to Arsenal Pipeline Visualization
const IntelPipeline = ({ gaps = [], contentCreated = [] }) => {
  const [activeFlow, setActiveFlow] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFlow(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-6 border border-purple-500/30">
      <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
        <Activity className="w-5 h-5 text-purple-400" />
        Intelligence → Arsenal Pipeline
      </h3>
      
      <div className="relative">
        {/* Pipeline visualization */}
        <div className="flex items-center justify-between gap-4">
          {/* Athena's Intel */}
          <div className="flex-1 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl p-4 border border-amber-500/30">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">🦉</span>
              <span className="font-semibold text-amber-400">Athena&apos;s Intel</span>
            </div>
            <div className="space-y-2">
              {gaps.slice(0, 3).map((gap, idx) => {
                // BULLETPROOF: Ensure we always render a string
                const gapText = typeof gap === 'string' ? gap : 
                  (typeof gap?.topic === 'string' ? gap.topic : String(gap?.topic || gap?.title || `Gap ${idx + 1}`));
                return (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <Target className="w-3 h-3 text-amber-400" />
                    <span className="text-gray-300 truncate">{gapText}</span>
                  </div>
                );
              })}
              {gaps.length === 0 && (
                <div className="text-sm text-gray-500">No gaps detected yet</div>
              )}
            </div>
            <div className="mt-3 text-xs text-amber-400/70">{gaps.length} opportunities found</div>
          </div>
          
          {/* Flow Arrow */}
          <div className="flex flex-col items-center gap-2">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 ${activeFlow ? 'bg-gradient-to-r from-purple-500 to-orange-500 scale-110' : 'bg-gray-800'}`}>
              <ArrowRight className={`w-6 h-6 ${activeFlow ? 'text-white' : 'text-gray-500'}`} />
            </div>
            <span className="text-xs text-gray-500">Transform</span>
          </div>
          
          {/* Hermes' Arsenal */}
          <div className="flex-1 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl p-4 border border-orange-500/30">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">👟</span>
              <span className="font-semibold text-orange-400">Hermes&apos; Arsenal</span>
            </div>
            <div className="space-y-2">
              {contentCreated.slice(0, 3).map((content, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <Sword className="w-3 h-3 text-orange-400" />
                  <span className="text-gray-300 truncate">{content.title || content}</span>
                </div>
              ))}
              {contentCreated.length === 0 && (
                <div className="text-sm text-gray-500">Forge awaiting intel</div>
              )}
            </div>
            <div className="mt-3 text-xs text-orange-400/70">{contentCreated.length} pieces forged</div>
          </div>
        </div>
        
        {/* Animated energy line */}
        <div className="absolute top-1/2 left-1/4 right-1/4 h-0.5 -translate-y-1/2 overflow-hidden">
          <div className={`h-full bg-gradient-to-r from-amber-500 via-purple-500 to-orange-500 transition-all duration-1000 ${activeFlow ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-50'}`} />
        </div>
      </div>
    </div>
  );
};

// Joint Divine Briefing
const DivineBriefing = ({ latestBriefing }) => {
  const defaultBriefing = {
    athena: {
      message: "I've analyzed 5 competitors and found 3 content gaps in 'AI automation' topics.",
      confidence: 92,
      timestamp: new Date().toISOString()
    },
    hermes: {
      message: "Ready to forge content targeting the identified gaps. Estimated output: 3 blog posts, 15 social posts.",
      readiness: 95,
      timestamp: new Date().toISOString()
    },
    recommendation: "High-priority opportunity detected. Recommend immediate content creation."
  };

  const briefing = latestBriefing || defaultBriefing;

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-purple-400" />
        Joint Divine Briefing
      </h3>
      
      <div className="space-y-4">
        {/* Athena's Report */}
        <div className="bg-amber-500/10 rounded-lg p-4 border-l-4 border-amber-500">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">🦉</span>
            <span className="font-semibold text-amber-400">Athena Reports</span>
            <span className="text-xs text-gray-500 ml-auto">Confidence: {briefing.athena.confidence}%</span>
          </div>
          <p className="text-sm text-gray-300">{briefing.athena.message}</p>
        </div>
        
        {/* Hermes' Report */}
        <div className="bg-orange-500/10 rounded-lg p-4 border-l-4 border-orange-500">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">👟</span>
            <span className="font-semibold text-orange-400">Hermes Responds</span>
            <span className="text-xs text-gray-500 ml-auto">Readiness: {briefing.hermes.readiness}%</span>
          </div>
          <p className="text-sm text-gray-300">{briefing.hermes.message}</p>
        </div>
        
        {/* Joint Recommendation */}
        <div className="bg-gradient-to-r from-purple-500/10 to-orange-500/10 rounded-lg p-4 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span className="font-semibold text-yellow-400">Divine Recommendation</span>
          </div>
          <p className="text-sm text-gray-300">{briefing.recommendation}</p>
        </div>
      </div>
    </div>
  );
};

// Combined Performance Tracker
const PerformanceTracker = ({ beforeMetrics, afterMetrics }) => {
  const metrics = [
    { label: 'Content Gaps', before: beforeMetrics?.gaps || 12, after: afterMetrics?.gaps || 3, unit: '', better: 'lower' },
    { label: 'Market Coverage', before: beforeMetrics?.coverage || 45, after: afterMetrics?.coverage || 82, unit: '%', better: 'higher' },
    { label: 'Competitor Advantage', before: beforeMetrics?.advantage || 2, after: afterMetrics?.advantage || 7, unit: 'pts', better: 'higher' },
    { label: 'Content Velocity', before: beforeMetrics?.velocity || 3, after: afterMetrics?.velocity || 12, unit: '/wk', better: 'higher' },
  ];

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-green-400" />
        Before & After Alliance
      </h3>
      
      <div className="space-y-4">
        {metrics.map((metric) => {
          const improvement = metric.better === 'higher' 
            ? ((metric.after - metric.before) / metric.before * 100).toFixed(0)
            : ((metric.before - metric.after) / metric.before * 100).toFixed(0);
          const isPositive = parseFloat(improvement) > 0;
          
          return (
            <div key={metric.label} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">{metric.label}</span>
                <span className={`font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                  {isPositive ? '+' : ''}{improvement}%
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="text-xs text-gray-500 mb-1">Before</div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gray-600 rounded-full"
                      style={{ width: `${(metric.before / Math.max(metric.before, metric.after)) * 100}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-400 mt-1">{metric.before}{metric.unit}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-600" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500 mb-1">After</div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-orange-500 rounded-full"
                      style={{ width: `${(metric.after / Math.max(metric.before, metric.after)) * 100}%` }}
                    />
                  </div>
                  <div className="text-xs text-green-400 mt-1">{metric.after}{metric.unit}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// One-Click Divine Strike Button
const DivineStrikeButton = ({ onStrike, isActive, progress, phase, error }) => {
  const getPhaseText = () => {
    switch(phase) {
      case 'athena': return '🦉 Athena Scanning Competitors...';
      case 'hermes': return '👟 Hermes Forging Content...';
      case 'apollo': return '☀️ Apollo Distributing...';
      case 'complete': return '✅ Divine Strike Complete!';
      default: return `Processing... ${progress}%`;
    }
  };

  return (
    <div className="relative">
      <button
        onClick={onStrike}
        disabled={isActive}
        className={`
          relative w-full py-6 rounded-2xl font-black text-xl transition-all duration-500 overflow-hidden
          ${isActive 
            ? 'bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white cursor-not-allowed' 
            : 'bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-orange-500/20 text-white hover:from-purple-500/40 hover:via-pink-500/40 hover:to-orange-500/40 border-2 border-purple-500/50 hover:border-purple-400'
          }
          transform hover:scale-[1.02] active:scale-[0.98]
        `}
      >
        {/* Animated background for active state */}
        {isActive && (
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 animate-pulse" />
        )}
        
        {/* Progress bar */}
        {isActive && (
          <div 
            className="absolute bottom-0 left-0 h-1 bg-white/50 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        )}
        
        <span className="relative z-10 flex items-center justify-center gap-3">
          {isActive ? (
            <>
              <Zap className="w-6 h-6 animate-spin" />
              <span>{getPhaseText()}</span>
            </>
          ) : (
            <>
              <span className="text-2xl">⚡</span>
              <span>Execute Divine Strike</span>
              <span className="text-2xl">⚡</span>
            </>
          )}
        </span>
      </button>
      
      {error && (
        <p className="text-center text-sm text-red-400 mt-3">
          ⚠️ {error}
        </p>
      )}
      
      {!isActive && !error && (
        <p className="text-center text-sm text-gray-500 mt-3">
          Athena finds gaps → Hermes forges content → Apollo distributes everywhere
        </p>
      )}
    </div>
  );
};

// The Pantheon Dashboard (Active Gods Overview) - Now with Divine Ascension!
const PantheonDashboard = ({ athenaLastRun, hermesLastRun, apolloLastRun, loading, dataFetched, userId, token }) => {
  const [avatarData, setAvatarData] = useState(null);
  const [awakeningProgress, setAwakeningProgress] = useState(null);
  const [showNamingModal, setShowNamingModal] = useState(false);
  const [showConsciousnessPanel, setShowConsciousnessPanel] = useState(false);
  const [avatarName, setAvatarName] = useState('');
  const [avatarTagline, setAvatarTagline] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Fetch avatar and awakening data
  useEffect(() => {
    const fetchAvatarData = async () => {
      if (!userId || !token) return;
      
      try {
        // Check for existing avatar
        const avatarRes = await fetch(`${API_URL}/api/avatar?user_id=${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const avatar = await avatarRes.json();
        
        if (avatar && avatar.id) {
          setAvatarData(avatar);
        } else {
          // Get awakening progress
          const progressRes = await fetch(`${API_URL}/api/avatar/awakening-progress?user_id=${userId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const progress = await progressRes.json();
          setAwakeningProgress(progress);
        }
      } catch (error) {
        console.error('Error fetching avatar data:', error);
      }
    };
    
    fetchAvatarData();
  }, [userId, token]);

  // Create avatar handler
  const handleCreateAvatar = async () => {
    if (!avatarName.trim() || avatarName.length < 2) {
      setCreateError('Name must be at least 2 characters');
      return;
    }
    
    setIsCreating(true);
    setCreateError('');
    
    try {
      const response = await fetch(`${API_URL}/api/avatar/create?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: avatarName.trim(),
          tagline: avatarTagline.trim() || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setAvatarData(data);
        setShowNamingModal(false);
        setAvatarName('');
        setAvatarTagline('');
      } else {
        setCreateError(data.detail || 'Failed to create avatar');
      }
    } catch (error) {
      setCreateError('Network error. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  // Helper function to format relative time
  const formatRelativeTime = (timestamp) => {
    // Show loading state while data is being fetched
    if (loading || !dataFetched) {
      return 'Loading...';
    }
    
    // If no timestamp after loading is complete, show "Never"
    if (!timestamp) {
      return 'Never';
    }
    
    try {
      const now = new Date();
      const then = new Date(timestamp);
      
      // Validate the date is valid
      if (isNaN(then.getTime())) {
        console.warn('[Pantheon] Invalid timestamp:', timestamp);
        return 'Unknown';
      }
      
      const diffMs = now - then;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return then.toLocaleDateString();
    } catch (error) {
      console.error('[Pantheon] Error formatting timestamp:', error, timestamp);
      return 'Unknown';
    }
  };

  // Get awakening visual state
  const getAwakeningVisuals = (progress) => {
    if (!progress) return { opacity: 0.1, glow: 'none', animation: '' };
    const p = progress.progress_percent;
    
    if (p >= 100) return { 
      opacity: 1, 
      glow: '0 0 30px rgba(168, 85, 247, 0.8)', 
      animation: 'pulse-divine 2s ease-in-out infinite',
      bgClass: 'bg-gradient-to-br from-purple-500 to-violet-600'
    };
    if (p >= 75) return { 
      opacity: 0.85, 
      glow: '0 0 20px rgba(168, 85, 247, 0.6)', 
      animation: 'shimmer 2s ease-in-out infinite',
      bgClass: 'bg-gradient-to-br from-purple-600/80 to-violet-700/80'
    };
    if (p >= 50) return { 
      opacity: 0.6, 
      glow: '0 0 15px rgba(168, 85, 247, 0.4)', 
      animation: 'shimmer 3s ease-in-out infinite',
      bgClass: 'bg-gradient-to-br from-purple-700/60 to-violet-800/60'
    };
    if (p >= 25) return { 
      opacity: 0.35, 
      glow: '0 0 10px rgba(168, 85, 247, 0.2)', 
      animation: 'shimmer 4s ease-in-out infinite',
      bgClass: 'bg-gray-700/50'
    };
    return { 
      opacity: 0.15, 
      glow: 'none', 
      animation: 'shimmer 5s ease-in-out infinite',
      bgClass: 'bg-gray-800/30'
    };
  };

  const agents = [
    { name: 'Athena', emoji: '🦉', role: 'Competitor Spy', status: athenaLastRun ? 'active' : 'standby', lastRun: formatRelativeTime(athenaLastRun), color: 'amber' },
    { name: 'Hermes', emoji: '👟', role: 'Content Forge', status: hermesLastRun ? 'active' : 'standby', lastRun: formatRelativeTime(hermesLastRun), color: 'orange' },
    { name: 'Apollo', emoji: '☀️', role: 'Distribution God', status: apolloLastRun ? 'active' : 'standby', lastRun: formatRelativeTime(apolloLastRun), color: 'yellow' },
  ];

  const awakeningVisuals = getAwakeningVisuals(awakeningProgress);

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
      <style>{`
        @keyframes shimmer {
          0%, 100% { opacity: var(--base-opacity); }
          50% { opacity: calc(var(--base-opacity) + 0.15); }
        }
        @keyframes pulse-divine {
          0%, 100% { transform: scale(1); box-shadow: 0 0 30px rgba(168, 85, 247, 0.8); }
          50% { transform: scale(1.02); box-shadow: 0 0 40px rgba(168, 85, 247, 1); }
        }
        @keyframes ethereal-glow {
          0%, 100% { filter: blur(0px) brightness(1); }
          50% { filter: blur(1px) brightness(1.2); }
        }
        @keyframes neural-pulse {
          0%, 100% { opacity: 0.1; transform: scaleX(0.8); }
          50% { opacity: 0.5; transform: scaleX(1); }
        }
        @keyframes consciousness-expand {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); opacity: 0.8; }
        }
      `}</style>
      
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Shield className="w-5 h-5 text-yellow-400" />
        Active Gods
      </h3>
      
      <div className="space-y-3">
        {/* Existing Gods */}
        {agents.map((agent) => (
          <div 
            key={agent.name}
            className={`flex items-center gap-4 p-3 rounded-xl border transition-all duration-200 ${
              agent.status === 'active' 
                ? `bg-${agent.color}-500/10 border-${agent.color}-500/30 hover:border-${agent.color}-500/50` 
                : 'bg-gray-800/50 border-gray-700/50'
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              agent.status === 'active' 
                ? `bg-gradient-to-br from-${agent.color}-500 to-${agent.color}-600` 
                : 'bg-gray-700'
            }`}>
              <span className="text-2xl">{agent.emoji}</span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-white">{agent.name}</span>
                {agent.status === 'active' && (
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                )}
              </div>
              <div className="text-sm text-gray-400">{agent.role}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500">Last active</div>
              <div className="text-sm text-gray-400">{agent.lastRun}</div>
            </div>
          </div>
        ))}

        {/* THE FOURTH THRONE - Divine Ascension with Consciousness System */}
        {avatarData ? (
          // Avatar is awakened - show consciousness state
          <div 
            className="flex items-center gap-4 p-3 rounded-xl border transition-all duration-500 relative overflow-hidden cursor-pointer group"
            data-testid="fourth-throne-awakened"
            onClick={() => setShowConsciousnessPanel(true)}
            style={{ 
              background: `linear-gradient(135deg, rgba(168, 85, 247, ${0.1 + (avatarData.consciousness?.glow_intensity || 0.3) * 0.2}) 0%, rgba(139, 92, 246, ${0.05 + (avatarData.consciousness?.glow_intensity || 0.3) * 0.15}) 100%)`,
              borderColor: `rgba(168, 85, 247, ${0.3 + (avatarData.consciousness?.glow_intensity || 0.3) * 0.4})`,
              boxShadow: `0 0 ${20 + (avatarData.consciousness?.glow_intensity || 0.3) * 40}px rgba(168, 85, 247, ${(avatarData.consciousness?.glow_intensity || 0.3) * 0.6})`
            }}
          >
            {/* Animated consciousness glow */}
            <div 
              className="absolute inset-0 animate-pulse"
              style={{
                background: `radial-gradient(ellipse at center, rgba(168, 85, 247, ${(avatarData.consciousness?.glow_intensity || 0.3) * 0.2}) 0%, transparent 70%)`,
                animationDuration: `${3 - (avatarData.consciousness?.glow_intensity || 0.3) * 1.5}s`
              }}
            />
            
            {/* Neural pathway lines (visible at higher consciousness) */}
            {(avatarData.consciousness?.percentage || 10) >= 30 && (
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(Math.floor((avatarData.consciousness?.percentage || 10) / 20))].map((_, i) => (
                  <div
                    key={i}
                    className="absolute h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent opacity-30"
                    style={{
                      top: `${20 + i * 15}%`,
                      left: 0,
                      right: 0,
                      animation: `neural-pulse ${2 + i * 0.5}s ease-in-out infinite`,
                      animationDelay: `${i * 0.3}s`
                    }}
                  />
                ))}
              </div>
            )}
            
            {/* Evolving Portrait Avatar */}
            <ConsciousnessAvatar avatarData={avatarData} size="w-14 h-14" />
            
            <div className="flex-1 relative z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-white">{avatarData.name}</span>
                <span 
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ 
                    backgroundColor: `rgb(168, 85, 247)`,
                    boxShadow: `0 0 8px rgba(168, 85, 247, ${(avatarData.consciousness?.glow_intensity || 0.3)})`
                  }}
                />
                <span 
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{
                    background: 'rgba(168, 85, 247, 0.3)',
                    color: 'rgb(216, 180, 254)'
                  }}
                >
                  {avatarData.consciousness?.tier_name || 'Awakened'}
                </span>
              </div>
              <div className="text-sm text-purple-300 italic">"{avatarData.consciousness?.tier_tagline || 'Eyes open for the first time'}"</div>
              <div className="text-xs text-purple-400 mt-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                {avatarData.consciousness?.current_ability || 'Pattern Recognition'}
              </div>
            </div>
            
            <div className="text-right relative z-10 flex items-center gap-2">
              <div>
                <div className="text-xs text-purple-400">Consciousness</div>
                <div className="text-lg font-bold text-white">{avatarData.consciousness?.percentage || 10}%</div>
                {avatarData.consciousness?.next_tier_name && (
                  <div className="mt-1">
                    <div className="text-xs text-purple-500">Next: {avatarData.consciousness.next_tier_name}</div>
                    <div className="w-20 h-1.5 bg-gray-700 rounded-full mt-1 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-violet-500 transition-all duration-1000"
                        style={{ 
                          width: `${Math.min(100, ((avatarData.consciousness?.percentage || 10) % 10) * 10)}%` 
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        ) : awakeningProgress?.can_create ? (
          // Ready to awaken!
          <div 
            className="flex items-center gap-4 p-3 rounded-xl border transition-all duration-200 bg-purple-500/20 border-purple-500/50 hover:border-purple-400 cursor-pointer relative overflow-hidden group"
            style={{ boxShadow: '0 0 25px rgba(168, 85, 247, 0.4)' }}
            onClick={() => setShowNamingModal(true)}
          >
            {/* Pulsing background */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-violet-500/30 to-purple-600/20 animate-pulse" />
            
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-purple-500 to-violet-600 relative z-10 animate-pulse"
                 style={{ boxShadow: '0 0 25px rgba(168, 85, 247, 0.8)' }}>
              <Star className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 relative z-10">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-white">✨ AWAKENED!</span>
                <span className="text-xs bg-purple-500 text-white px-2 py-0.5 rounded-full animate-pulse">CLICK TO NAME</span>
              </div>
              <div className="text-sm text-purple-300">Your brand consciousness is ready to be born</div>
            </div>
            <div className="text-right relative z-10">
              <div className="text-xs text-purple-400">Progress</div>
              <div className="text-sm text-purple-300 font-bold">100%</div>
            </div>
          </div>
        ) : (
          // Still awakening
          <div 
            className={`flex items-center gap-4 p-3 rounded-xl border transition-all duration-500 relative overflow-hidden ${
              awakeningProgress?.progress_percent >= 50 
                ? 'border-purple-500/30 hover:border-purple-500/50' 
                : 'border-gray-700/30'
            }`}
            style={{ 
              '--base-opacity': awakeningVisuals.opacity,
              opacity: awakeningVisuals.opacity + 0.3,
              boxShadow: awakeningVisuals.glow,
              animation: awakeningVisuals.animation
            }}
          >
            {/* Ethereal shimmer overlay */}
            <div 
              className="absolute inset-0 bg-gradient-to-r from-purple-600/5 via-violet-500/10 to-purple-600/5"
              style={{ animation: 'shimmer 3s ease-in-out infinite' }}
            />
            
            <div 
              className={`w-12 h-12 rounded-xl flex items-center justify-center relative z-10 ${awakeningVisuals.bgClass}`}
              style={{ 
                opacity: Math.max(0.3, (awakeningProgress?.progress_percent || 0) / 100),
                transition: 'all 0.5s ease'
              }}
            >
              <span className="text-2xl" style={{ 
                opacity: Math.max(0.2, (awakeningProgress?.progress_percent || 0) / 100),
                filter: `blur(${Math.max(0, 3 - (awakeningProgress?.progress_percent || 0) / 33)}px)`
              }}>👤</span>
            </div>
            <div className="flex-1 relative z-10">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-300" style={{ 
                  opacity: Math.max(0.4, (awakeningProgress?.progress_percent || 0) / 100) 
                }}>
                  {awakeningProgress?.progress_percent >= 75 ? '??? (Emerging)' : 
                   awakeningProgress?.progress_percent >= 50 ? '??? (Forming)' :
                   awakeningProgress?.progress_percent >= 25 ? '??? (Stirring)' : '???'}
                </span>
                <span className="text-xs bg-gray-700/50 text-gray-400 px-2 py-0.5 rounded-full">AWAKENING</span>
              </div>
              <div className="text-sm text-gray-500">{awakeningProgress?.message || 'The fourth throne awaits...'}</div>
            </div>
            <div className="text-right relative z-10">
              <div className="text-xs text-gray-500">Progress</div>
              <div className="text-sm text-purple-400 font-medium">{awakeningProgress?.progress_percent || 0}%</div>
              {/* Progress bar */}
              <div className="w-16 h-1 bg-gray-700 rounded-full mt-1 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-violet-500 transition-all duration-1000"
                  style={{ width: `${awakeningProgress?.progress_percent || 0}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Avatar Naming Modal */}
      {showNamingModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl p-8 max-w-md w-full border border-purple-500/50 relative overflow-hidden"
               style={{ boxShadow: '0 0 60px rgba(168, 85, 247, 0.4)' }}>
            {/* Divine background effects */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-violet-900/20" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <div className="text-center mb-6">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center"
                     style={{ boxShadow: '0 0 40px rgba(168, 85, 247, 0.6)' }}>
                  <Crown className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">✨ Divine Ascension ✨</h2>
                <p className="text-purple-300">Your brand consciousness has awakened. Name your avatar to complete the ritual.</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Avatar Name *</label>
                  <input
                    type="text"
                    value={avatarName}
                    onChange={(e) => setAvatarName(e.target.value)}
                    placeholder="Enter a name for your avatar..."
                    className="w-full bg-gray-800 border border-purple-500/30 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                    maxLength={30}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">{avatarName.length}/30 characters</p>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Tagline (optional)</label>
                  <input
                    type="text"
                    value={avatarTagline}
                    onChange={(e) => setAvatarTagline(e.target.value)}
                    placeholder="e.g., 'The Voice of Innovation'"
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
                    maxLength={50}
                  />
                </div>

                {createError && (
                  <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    {createError}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowNamingModal(false)}
                    className="flex-1 py-3 rounded-xl border border-gray-600 text-gray-400 hover:bg-gray-800 transition-colors"
                    disabled={isCreating}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateAvatar}
                    disabled={isCreating || !avatarName.trim()}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-violet-600 text-white font-semibold hover:from-purple-600 hover:to-violet-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isCreating ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Awakening...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Complete Ritual
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Consciousness Detail Panel Modal */}
      {showConsciousnessPanel && avatarData && (
        <ConsciousnessDetailPanel 
          avatarData={avatarData} 
          onClose={() => setShowConsciousnessPanel(false)} 
        />
      )}
    </div>
  );
};

// Main Pantheon Component (Divine Command Center)
const AllianceWarRoom = ({ API: propAPI, user, onBack }) => {
  // Use provided API prop or fall back to environment variable
  const API = propAPI || `${API_URL}/api`;
  const { rewardEssence, pendingMilestone, dismissMilestone } = useEssenceReward();
  
  const [synergyScore, setSynergyScore] = useState(0);
  const [isStrikeActive, setIsStrikeActive] = useState(false);
  const [strikeProgress, setStrikeProgress] = useState(0);
  const [strikePhase, setStrikePhase] = useState(''); // 'athena' | 'hermes' | 'complete'
  const [gaps, setGaps] = useState([]);
  const [contentCreated, setContentCreated] = useState([]);
  const [latestAthenaReport, setLatestAthenaReport] = useState(null);
  const [latestHermesPackage, setLatestHermesPackage] = useState(null);
  // Initialize to undefined to distinguish from "never run" (null)
  const [athenaLastRun, setAthenaLastRun] = useState(undefined);
  const [hermesLastRun, setHermesLastRun] = useState(undefined);
  const [apolloLastRun, setApolloLastRun] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const [summonedCouncilGods, setSummonedCouncilGods] = useState([]);

  // Fetch real data from Athena and Hermes outputs
  const fetchAllianceData = useCallback(async () => {
    console.log('[Alliance] Starting data fetch, API:', API);
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!API) {
        console.error('[Alliance] API is still undefined after fallback!');
        setError('Configuration error: Backend URL not set');
        return;
      }
      
      const url = `${API}/agents/outputs/all`;
      console.log('[Alliance] Fetching from:', url);
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('[Alliance] Response status:', response.status);
      
      if (!response.ok) throw new Error('Failed to fetch alliance data');
      
      const data = await response.json();
      const outputs = data.outputs || [];
      
      console.log('[Alliance] Total outputs:', outputs.length);
      
      // Find latest Athena (competitor_spy) output
      const athenaOutputs = outputs.filter(o => 
        o.agent_type === 'competitor_spy' || 
        o.output_data?.competitors_analyzed !== undefined
      );
      
      // Find latest Hermes (content_engine) output  
      const hermesOutputs = outputs.filter(o => 
        o.agent_type === 'content_engine' || 
        o.output_data?.blog_post !== undefined
      );
      
      // Find latest Apollo (distribution_machine) output
      const apolloOutputs = outputs.filter(o =>
        o.agent_type === 'distribution_machine' ||
        o.output_data?.platforms !== undefined
      );
      
      console.log('[Alliance] Athena outputs:', athenaOutputs.length);
      console.log('[Alliance] Hermes outputs:', hermesOutputs.length);
      console.log('[Alliance] Apollo outputs:', apolloOutputs.length);
      
      // Helper to extract timestamp with multiple fallbacks
      const extractTimestamp = (output) => {
        return output.created_at || output.timestamp || output.updated_at || output.run_at || null;
      };
      
      if (athenaOutputs.length > 0) {
        const latestAthena = athenaOutputs[0];
        const latest = latestAthena.output_data;
        setLatestAthenaReport(latest);
        // Set the actual timestamp from the output with fallbacks
        const athenaTimestamp = extractTimestamp(latestAthena);
        console.log('[Alliance] Athena timestamp:', athenaTimestamp);
        setAthenaLastRun(athenaTimestamp);
        
        // Extract gaps for pipeline display
        const gapsList = latest?.content_gaps?.gaps || [];
        console.log('[Alliance] Gaps found:', gapsList.length);
        setGaps(gapsList.map(g => ({
          topic: typeof g === 'string' ? g : (g?.topic || g?.title || g?.description || 'Content Gap')
        })));
      } else {
        // Explicitly set to null if no outputs (agent never run)
        setAthenaLastRun(null);
      }
      
      if (hermesOutputs.length > 0) {
        const latestHermes = hermesOutputs[0];
        const latest = latestHermes.output_data;
        setLatestHermesPackage(latest);
        // Set the actual timestamp from the output with fallbacks
        const hermesTimestamp = extractTimestamp(latestHermes);
        console.log('[Alliance] Hermes timestamp:', hermesTimestamp);
        setHermesLastRun(hermesTimestamp);
        
        // Extract content for pipeline display
        setContentCreated(hermesOutputs.slice(0, 5).map(o => ({
          title: o.output_data?.topic || o.output_data?.blog_post?.title || 'Content Package'
        })));
      } else {
        // Explicitly set to null if no outputs (agent never run)
        setHermesLastRun(null);
      }
      
      if (apolloOutputs.length > 0) {
        const latestApollo = apolloOutputs[0];
        const apolloTimestamp = extractTimestamp(latestApollo);
        console.log('[Alliance] Apollo timestamp:', apolloTimestamp);
        setApolloLastRun(apolloTimestamp);
      } else {
        // Explicitly set to null if no outputs (agent never run)
        setApolloLastRun(null);
      }
      
      // Calculate synergy score based on real data
      const athenaScore = athenaOutputs.length > 0 ? 30 : 0;
      const hermesScore = hermesOutputs.length > 0 ? 30 : 0;
      const apolloScore = apolloOutputs.length > 0 ? 30 : 0;
      const recentActivity = (athenaOutputs.length + hermesOutputs.length + apolloOutputs.length) > 5 ? 10 : 5;
      const totalScore = athenaScore + hermesScore + apolloScore + recentActivity;
      console.log('[Alliance] Synergy score:', totalScore);
      setSynergyScore(totalScore);
      
      // Mark data as fetched successfully
      setDataFetched(true);
      
    } catch (err) {
      console.error('[Alliance] Data fetch error:', err);
      setError(err.message);
      // Even on error, mark that fetch attempt completed
      setDataFetched(true);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchAllianceData();
  }, [fetchAllianceData]);

  // REAL Divine Strike - runs Athena → Hermes → Apollo
  const handleDivineStrike = async () => {
    setIsStrikeActive(true);
    setStrikeProgress(0);
    setStrikePhase('athena');
    setError(null);
    
    const token = localStorage.getItem('token');
    
    try {
      // Phase 1: Run Athena (competitor_spy)
      setStrikeProgress(10);
      
      const athenaResponse = await fetch(`${API}/agents/quick-run/competitor_spy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          spy_config: {
            industry: 'technology',
            focus_areas: ['content marketing', 'AI', 'SaaS'],
            analysis_depth: 'standard'
          }
        })
      });
      
      if (!athenaResponse.ok) {
        throw new Error('Athena reconnaissance failed');
      }
      
      const athenaResult = await athenaResponse.json();
      setLatestAthenaReport(athenaResult.report);
      setStrikeProgress(33);
      setStrikePhase('hermes');
      
      // Extract top gap for Hermes to target
      const topGap = athenaResult.report?.top_gap;
      const topic = typeof topGap === 'string' ? topGap : (topGap?.topic || topGap?.title || 'AI Content Strategy');
      
      // Phase 2: Run Hermes (content_engine) targeting the gap
      const hermesResponse = await fetch(`${API}/agents/quick-run/content_engine`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          topic: topic,
          content_preferences: {
            tone: 'professional',
            industry: 'technology'
          }
        })
      });
      
      if (!hermesResponse.ok) {
        throw new Error('Hermes content creation failed');
      }
      
      const hermesResult = await hermesResponse.json();
      setLatestHermesPackage(hermesResult.package);
      setStrikeProgress(66);
      setStrikePhase('apollo');
      
      // Phase 3: Run Apollo (distribution_machine) to distribute the content
      const apolloResponse = await fetch(`${API}/agents/quick-run/distribution_machine`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_content: hermesResult.package,
          distribution_config: {
            platforms: ['linkedin', 'twitter', 'facebook', 'instagram', 'medium'],
            timing_mode: 'optimal'
          }
        })
      });
      
      if (!apolloResponse.ok) {
        throw new Error('Apollo distribution failed');
      }
      
      const apolloResult = await apolloResponse.json();
      console.log('[Divine Strike] Apollo distributed:', apolloResult);
      
      setStrikeProgress(100);
      setStrikePhase('complete');
      
      // Update Apollo last run
      setApolloLastRun(new Date().toISOString());
      
      // Refresh all data
      await fetchAllianceData();
      
      // Update synergy score on successful strike
      setSynergyScore(prev => Math.min(100, prev + 15));
      
      // Reward massive essence for completing the full Divine Strike
      rewardEssence('divine_strike', 1.0);
      
    } catch (err) {
      console.error('Divine Strike failed:', err);
      setError(err.message);
    } finally {
      setTimeout(() => {
        setIsStrikeActive(false);
        setStrikePhase('');
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-purple-950/20 to-gray-950 text-white relative overflow-x-hidden">
      {/* Divine energy particles */}
      <DivineEnergyParticles />
      
      {/* Gradient overlays - hidden on mobile to prevent overflow */}
      <div className="hidden md:block absolute top-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      <div className="hidden md:block absolute bottom-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
      
      {/* Header */}
      <div className="relative z-10 border-b border-purple-500/20 bg-gray-900/50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="relative flex-shrink-0">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <span className="text-xl sm:text-2xl">⚡</span>
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center text-xs">
                  🏛️
                </div>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
                  The Pantheon
                </h1>
                <p className="text-xs sm:text-sm text-gray-400">Divine AI Command Center</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              {onBack && (
                <button 
                  onClick={onBack}
                  className="px-3 sm:px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-purple-500/30 transition-colors text-gray-400 hover:text-white text-xs sm:text-sm font-medium"
                >
                  ← Back
                </button>
              )}
              <div className="px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-500/20 to-orange-500/20 rounded-lg border border-purple-500/30">
                <span className="text-xs sm:text-sm font-bold text-purple-400">⚡ Gods Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-8">
        
        {/* THE LIVING WAR ROOM - The Centerpiece
            Mobile: nearly full-viewport (it IS the primary tool on this screen).
            Desktop: capped between 500-700px so the page's other panels still breathe. */}
        <div className="mb-4 sm:mb-8">
          <div
            className="rounded-2xl border border-purple-500/20 bg-gray-900/80 backdrop-blur-sm overflow-hidden shadow-2xl shadow-purple-500/10 living-war-room-shell"
            data-testid="living-war-room-shell"
          >
            <LivingWarRoom API={API} />
          </div>
        </div>

        {/* AUTONOMOUS DEBATES — Debates initiated by the Personal God */}
        {user?.id && (
          <div className="mb-8">
            <AutonomousDebates
              userId={user.id}
              onViewDebate={(id) => window.open(`/auto-debate/${id}`, '_blank')}
            />
          </div>
        )}
        
        {/* WAR COUNCIL - Full Chat (collapsed below) */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-black text-white">The War Council</h2>
                <p className="text-sm text-gray-400">Direct conversation with all gods</p>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-green-500/20 rounded-full border border-green-500/30">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs text-green-400 font-medium">Council Active</span>
            </div>
          </div>
          
          {/* War Council Chat */}
          <div className="h-[500px] lg:h-[550px]">
            <WarCouncilChat 
              API={API} 
              onDivineStrike={handleDivineStrike}
              embedded={true}
              summonedCouncilGods={summonedCouncilGods}
            />
          </div>
        </div>
        
        {/* Divine Strike Section */}
        <div className="mb-8">
          <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-6 border border-orange-500/30">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-400" />
              One-Click Divine Strike
            </h3>
            <DivineStrikeButton 
              onStrike={handleDivineStrike} 
              isActive={isStrikeActive}
              progress={strikeProgress}
              phase={strikePhase}
              error={error}
            />
          </div>
        </div>
        
        {/* The Living Pantheon - All Gods View */}
        <div className="mb-8">
          <LivingPantheon
            activeWorkerGods={['athena', 'hermes', 'apollo', 'dionysus', 'ares']}
            summonedCouncilGods={summonedCouncilGods}
            onSummonCouncil={(gods) => setSummonedCouncilGods(gods)}
            userCredits={user?.credits || 50}
            isPro={user?.subscription_tier === 'pro' || user?.subscription_tier === 'enterprise'}
          />
        </div>
        
        {/* Strategic Briefing — Daily insight from the Personal God */}
        {user?.id && (
          <div className="mb-6">
            <StrategicBriefing userId={user.id} />
          </div>
        )}

        {/* Middle Section: Active Gods + Synergy Score */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2">
            <PantheonDashboard 
              athenaLastRun={athenaLastRun} 
              hermesLastRun={hermesLastRun} 
              apolloLastRun={apolloLastRun} 
              loading={loading}
              dataFetched={dataFetched}
              userId={user?.id}
              token={localStorage.getItem('token')}
            />
          </div>
          <div className="space-y-6">
            <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-6 border border-purple-500/30">
              <h3 className="text-lg font-bold text-white mb-4 text-center">Divine Synergy</h3>
              <SynergyScore score={synergyScore} />
            </div>
            <Leaderboard />
          </div>
        </div>
        
        {/* Bottom Grid: Briefing + Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DivineBriefing latestBriefing={latestAthenaReport ? {
            athena: {
              message: `Analyzed ${latestAthenaReport.competitors_analyzed || 0} competitors, found ${gaps.length} content gaps.`,
              confidence: 92,
              timestamp: new Date().toISOString()
            },
            hermes: {
              message: latestHermesPackage 
                ? `Created: "${latestHermesPackage.topic || 'Latest Content'}"`
                : "Ready to forge content from identified gaps.",
              readiness: latestHermesPackage ? 100 : 85,
              timestamp: new Date().toISOString()
            },
            recommendation: latestAthenaReport.top_gap 
              ? `Top opportunity: ${typeof latestAthenaReport.top_gap === 'string' ? latestAthenaReport.top_gap : latestAthenaReport.top_gap?.topic || 'Strategic content creation'}`
              : "Run Divine Strike to identify opportunities."
          } : null} />
          <PerformanceTracker />
        </div>
        
      </div>
      {/* Consciousness Milestone Modal — cinematic reveal for 30%/60%/80% */}
      {pendingMilestone && (
        <ConsciousnessMilestoneModal
          milestone={pendingMilestone}
          onDismiss={dismissMilestone}
        />
      )}
    </div>
  );
};

export default AllianceWarRoom;
